/**
 * Zerlegung von Text in Abschnitte für die Einbettung.
 *
 * Das Auslesen des Textes selbst ist nach `aufbereitung/` umgezogen: Es hängt
 * dort untrennbar an Zuschnitt, Entzerrung und Texterkennung – ein PDF ohne
 * Textebene bekommt heute eine, statt textlos abgelegt zu werden. Was hier
 * bleibt, ist der Schritt danach.
 */

/**
 * Zerlegt einen Text in Abschnitte, die das Einbettungsmodell verarbeiten kann.
 *
 * Das Modell nimmt 512 Tokens auf einmal. Ein Token entspricht im Deutschen
 * grob drei bis vier Zeichen, deshalb rund 1.500 Zeichen je Abschnitt mit
 * Sicherheitsabstand.
 *
 * Die Überlappung ist wichtig: Ohne sie kann ein Satz genau an der Grenze
 * zerschnitten werden, und die Aussage („Onkel Helmut hatte am 3. Mai seine
 * OP") wäre in keinem Abschnitt vollständig enthalten – gefunden würde sie
 * dann nicht mehr.
 *
 * Getrennt wird bevorzugt an Absatz- und Satzgrenzen, damit ein Abschnitt
 * inhaltlich zusammenhängt.
 */
export function splitIntoChunks(
  text: string,
  maxChars = 1500,
  overlapChars = 250,
): string[] {
  const normalized = text.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxChars) return normalized ? [normalized] : [];

  const chunks: string[] = [];
  let start = 0;

  while (start < normalized.length) {
    let end = Math.min(start + maxChars, normalized.length);

    if (end < normalized.length) {
      // Rückwärts eine natürliche Grenze suchen: erst Satzende, dann Leerzeichen.
      const fenster = normalized.slice(start, end);
      const satz = Math.max(
        fenster.lastIndexOf('. '),
        fenster.lastIndexOf('! '),
        fenster.lastIndexOf('? '),
      );
      const grenze = satz > maxChars * 0.5 ? satz + 1 : fenster.lastIndexOf(' ');
      if (grenze > maxChars * 0.5) end = start + grenze;
    }

    const chunk = normalized.slice(start, end).trim();
    if (chunk) chunks.push(chunk);
    if (end >= normalized.length) break;
    start = Math.max(end - overlapChars, start + 1);
  }

  return chunks;
}
